import React from "react";
import OrderRes from "types/res/order/OrderRes";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";

type Props = {
  orders: OrderRes[];
  message: string;
};

/**
 * 支払通知書の HTML 版 (html2canvas で画像化 → jsPDF で PDF 化)。
 * 画面には表示されず、キャプチャ用に off-screen でレンダリングされる。
 */
const SpecPdf = React.forwardRef<HTMLDivElement, Props>(
  ({ orders, message }, ref) => {
    if (!orders.length) return null;

    const sum = (key: keyof OrderRes) =>
      orders.reduce((s, o) => s + ((o[key] as number) || 0), 0);

    const borderStyle: React.CSSProperties = {
      border: "1px solid #bfbfbf",
      borderBottom: "1px solid #000",
    };

    const cellStyle: React.CSSProperties = {
      padding: "6px 8px",
      fontSize: 10,
      border: "1px solid #bfbfbf",
      textAlign: "right",
      verticalAlign: "middle",
    };

    const cellTextStyle: React.CSSProperties = {
      ...cellStyle,
      textAlign: "left",
    };

    const headerCellStyle: React.CSSProperties = {
      ...cellStyle,
      fontSize: 11,
      fontWeight: "bold",
      textAlign: "center",
      backgroundColor: "#f5f5f5",
      ...borderStyle,
    };

    return (
      <div
        ref={ref}
        style={{
          width: 1800,
          padding: 24,
          backgroundColor: "#fff",
          fontFamily:
            "'Yu Gothic', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
          color: "#000",
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            fontSize: 28,
            fontWeight: "bold",
            margin: "10px 0 20px",
          }}
        >
          支払通知書
        </h1>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr>
              <th style={headerCellStyle}>日付</th>
              <th style={headerCellStyle}>顧客名</th>
              <th style={headerCellStyle}>店名</th>
              <th style={headerCellStyle}>源氏名</th>
              <th style={headerCellStyle}>コース名</th>
              <th style={headerCellStyle}>コース</th>
              <th style={headerCellStyle}>延長</th>
              <th style={headerCellStyle}>指名</th>
              <th style={headerCellStyle}>その他</th>
              <th style={headerCellStyle}>交通費</th>
              <th style={headerCellStyle}>割引・イベント</th>
              <th style={headerCellStyle}>媒体</th>
              <th style={headerCellStyle}>コスプレ</th>
              <th style={headerCellStyle}>オプション</th>
              <th style={headerCellStyle}>厚生費</th>
              <th style={headerCellStyle}>インボイス</th>
              <th style={headerCellStyle}>合計</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.orderId}>
                <td style={cellStyle}>
                  {DateTimeUtils.toFormatAsLocalTimezone(
                    order.orderDate,
                    FORMAT_TYPE.YEAR_DAY
                  )}
                </td>
                <td style={cellTextStyle}>{order?.guest?.name || ""}</td>
                <td style={cellTextStyle}>{order?.shop?.name || ""}</td>
                <td style={cellTextStyle}>
                  {order?.castName?.name}
                  {order?.castName?.cast?.name
                    ? ` (${order.castName.cast.name})`
                    : ""}
                </td>
                <td style={cellTextStyle}>{order?.course?.name || ""}</td>
                <td style={cellStyle}>{order?.courseCastFee || 0}円</td>
                <td style={cellStyle}>{order?.additionalCastFee || 0}円</td>
                <td style={cellStyle}>{order?.designateCastFee || 0}円</td>
                <td style={cellStyle}>{order?.otherCastFee || 0}円</td>
                <td style={cellStyle}>{order?.areaCastFee || 0}円</td>
                <td style={cellStyle}>{order?.discountCastFee || 0}円</td>
                <td style={cellStyle}>{order?.mediumCastFee || 0}円</td>
                <td style={cellStyle}>{order?.cosplayCastFee || 0}円</td>
                <td style={cellStyle}>{order?.optionCastFee || 0}円</td>
                <td style={cellStyle}>{order?.welfareFee || 0}円</td>
                <td style={cellStyle}>{order?.invoiceFee || 0}円</td>
                <td style={cellStyle}>{order?.totalCastFee || 0}円</td>
              </tr>
            ))}
            <tr>
              <td
                colSpan={5}
                style={{ ...cellStyle, textAlign: "right", fontWeight: "bold" }}
              >
                合計
              </td>
              <td style={cellStyle}>{sum("courseCastFee")}円</td>
              <td style={cellStyle}>{sum("additionalCastFee")}円</td>
              <td style={cellStyle}>{sum("designateCastFee")}円</td>
              <td style={cellStyle}>{sum("otherCastFee")}円</td>
              <td style={cellStyle}>{sum("areaCastFee")}円</td>
              <td style={cellStyle}>{sum("discountCastFee")}円</td>
              <td style={cellStyle}>{sum("mediumCastFee")}円</td>
              <td style={cellStyle}>{sum("cosplayCastFee")}円</td>
              <td style={cellStyle}>{sum("optionCastFee")}円</td>
              <td style={cellStyle}>{sum("welfareFee")}円</td>
              <td style={cellStyle}>{sum("invoiceFee")}円</td>
              <td style={cellStyle}>{sum("totalCastFee")}円</td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            fontSize: 16,
            textAlign: "right",
            marginTop: 30,
            marginRight: 5,
          }}
        >
          企業名：{orders?.[0]?.company?.name || ""}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: "bold",
            marginLeft: 5,
            marginTop: 20,
          }}
        >
          キャストの方へのメッセージ
        </div>
        <div
          style={{
            padding: 10,
            border: "1px solid #000",
            margin: 5,
            fontSize: 12,
            minHeight: 60,
            whiteSpace: "pre-wrap",
          }}
        >
          {message}
        </div>
      </div>
    );
  }
);

SpecPdf.displayName = "SpecPdf";
export default SpecPdf;
